/**
 * Earliest fiscal year with data in the system.
 * Used as the lower bound for FY dropdowns and range helpers.
 */
export const EARLIEST_FISCAL_YEAR = 2026;

export type Quarter = 'Q1' | 'Q2' | 'Q3' | 'Q4';

export interface QuarterInfo {
  quarter: Quarter;
  fiscalYear: number;
  startDate: Date;
  endDate: Date;
  isAvailable: boolean;
  label: string;
}

/**
 * Get the current fiscal year
 * Fiscal year runs July-June
 * FY26 = July 2025 - June 2026
 */
export function getCurrentFiscalYear(): number {
  const now = new Date();
  const currentMonth = now.getMonth(); // 0-11
  const currentYear = now.getFullYear();
  
  // If month is July (6) or later, FY is current year + 1
  // If month is before July, FY is current year
  return currentMonth >= 6 ? currentYear + 1 : currentYear;
}

/**
 * Get the default fiscal year with a one-quarter lag.
 * Stays on the PREVIOUS FY until the first day of Q2 (1 October),
 * then rolls over to the current FY.
 */
export function getDefaultFiscalYear(): number {
  const now = new Date();
  const currentMonth = now.getMonth(); // 0-11
  const currentYear = now.getFullYear();

  // Roll over to next FY only when month >= October (index 9)
  return currentMonth >= 9 ? currentYear + 1 : currentYear;
}

/**
 * Format a fiscal year number as a two-digit tag (e.g. 2026 → "FY26").
 */
export function formatFiscalYear(fiscalYear: number): string {
  return `FY${String(fiscalYear % 100).padStart(2, '0')}`;
}

/**
 * Get the list of fiscal years available in the FY dropdown.
 * Returns a range from EARLIEST_FISCAL_YEAR up to getDefaultFiscalYear() + 1.
 */
export function getSelectableFiscalYears(): number[] {
  const maxFy = getDefaultFiscalYear() + 1;
  const years: number[] = [];
  for (let fy = EARLIEST_FISCAL_YEAR; fy <= maxFy; fy++) {
    years.push(fy);
  }
  return years;
}

/**
 * Get the start date for a specific quarter in a fiscal year
 */
export function getQuarterStartDate(fiscalYear: number, quarter: Quarter): Date {
  const startYear = fiscalYear - 1; // FY26 starts in 2025
  
  const quarterStartDates: Record<Quarter, Date> = {
    Q1: new Date(startYear, 6, 1),  // 1st July
    Q2: new Date(startYear, 9, 1),  // 1st October
    Q3: new Date(fiscalYear, 0, 1), // 1st January
    Q4: new Date(fiscalYear, 3, 1), // 1st April
  };
  
  return quarterStartDates[quarter];
}

/**
 * Get the end date for a specific quarter in a fiscal year
 */
export function getQuarterEndDate(fiscalYear: number, quarter: Quarter): Date {
  const startYear = fiscalYear - 1;
  
  const quarterEndDates: Record<Quarter, Date> = {
    Q1: new Date(startYear, 8, 30),  // 30th September
    Q2: new Date(startYear, 11, 31), // 31st December
    Q3: new Date(fiscalYear, 2, 31), // 31st March
    Q4: new Date(fiscalYear, 5, 30), // 30th June
  };
  
  return quarterEndDates[quarter];
}

/**
 * Check if a quarter is available (has closed/ended)
 * A quarter becomes available for review only after its end date has passed
 */
export function isQuarterAvailable(fiscalYear: number, quarter: Quarter): boolean {
  const now = new Date();
  const endDate = getQuarterEndDate(fiscalYear, quarter);
  const dayAfterEnd = new Date(endDate);
  dayAfterEnd.setDate(dayAfterEnd.getDate() + 1);
  return now >= dayAfterEnd;
}

/**
 * Format a date as "1st January 2026"
 */
export function formatAvailabilityDate(date: Date): string {
  const day = date.getDate();
  const month = date.toLocaleString('en-GB', { month: 'long', timeZone: 'Europe/London' });
  const year = date.getFullYear();
  
  const suffix = getDaySuffix(day);
  
  return `${day}${suffix} ${month} ${year}`;
}

function getDaySuffix(day: number): string {
  if (day >= 11 && day <= 13) return 'th';
  
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
}

/**
 * Get all quarters for a fiscal year with their availability status.
 * Defaults to getDefaultFiscalYear() when no argument is provided.
 */
export function getAllQuarters(fiscalYear?: number): QuarterInfo[] {
  const fy = fiscalYear ?? getDefaultFiscalYear();
  const quarters: Quarter[] = ['Q1', 'Q2', 'Q3', 'Q4'];
  
  return quarters.map(quarter => ({
    quarter,
    fiscalYear: fy,
    startDate: getQuarterStartDate(fy, quarter),
    endDate: getQuarterEndDate(fy, quarter),
    isAvailable: isQuarterAvailable(fy, quarter),
    label: `${formatFiscalYear(fy)} ${quarter}`,
  }));
}

export function getVisibleQuarters(fiscalYear: number, includeOpenQuarters: boolean): QuarterInfo[] {
  const quarters = getAllQuarters(fiscalYear);
  return includeOpenQuarters
    ? quarters.map((quarter) => ({ ...quarter, isAvailable: true }))
    : quarters.filter((quarter) => quarter.isAvailable);
}

/**
 * Get all quarters for a range of fiscal years (oldest-first).
 * Returns Q1–Q4 for every FY from startFiscalYear up to and including
 * endFiscalYear (defaults to getCurrentFiscalYear()).
 */
export function getAllQuartersFrom(startFiscalYear: number, endFiscalYear?: number): QuarterInfo[] {
  const end = endFiscalYear ?? getCurrentFiscalYear();
  const quarterNames: Quarter[] = ['Q1', 'Q2', 'Q3', 'Q4'];
  const result: QuarterInfo[] = [];

  for (let fy = startFiscalYear; fy <= end; fy++) {
    for (const quarter of quarterNames) {
      result.push({
        quarter,
        fiscalYear: fy,
        startDate: getQuarterStartDate(fy, quarter),
        endDate: getQuarterEndDate(fy, quarter),
        isAvailable: isQuarterAvailable(fy, quarter),
        label: `${formatFiscalYear(fy)} ${quarter}`,
      });
    }
  }

  return result;
}

/**
 * Get the fiscal year quarter string (e.g., "FY26 Q1")
 */
export function getFiscalYearQuarterString(fiscalYear: number, quarter: Quarter): string {
  return `${formatFiscalYear(fiscalYear)} ${quarter}`;
}
